import { previewStack } from '../setup/previewStack';
import { PlainObject } from '../helpers/helpers';
import { visitUnresolvedReferences } from './visitUnresolvedReferences';

const MAX_REFERENCE_DEPTH = 4;
const UIDS_PER_REQUEST = 50;

const splitIntoGroups = <T>(items: T[], groupSize: number): T[][] =>
  items.reduce<T[][]>((groups, item, index) => {
    if (index % groupSize === 0) groups.push([]);
    groups[groups.length - 1].push(item);

    return groups;
  }, []);

const fetchEntries = async (
  contentTypeUid: string,
  uids: string[],
  locale?: string
): Promise<PlainObject[]> => {
  try {
    const query = previewStack.ContentType(contentTypeUid).Query();

    if (locale) query.language(locale);

    const response = await query
      .containedIn('uid', uids)
      .addParam('include_dimension', 'true')
      .toJSON()
      .find();

    const entries: PlainObject[] =
      (Array.isArray(response?.[0]) ? response[0] : response) ?? [];

    return entries.map((entry) => ({
      ...entry,
      _content_type_uid: contentTypeUid,
    }));
  } catch (error) {
    console.warn(
      `[live-preview] could not resolve ${contentTypeUid} references:`,
      error
    );

    return [];
  }
};

const collectUidsByContentType = (
  rootEntry: PlainObject
): Map<string, Set<string>> => {
  const uidsByContentType = new Map<string, Set<string>>();

  visitUnresolvedReferences(rootEntry, (reference, parentEntryUids) => {
    if (parentEntryUids.has(reference.uid)) return;

    const uids =
      uidsByContentType.get(reference._content_type_uid) ?? new Set<string>();

    uids.add(reference.uid);
    uidsByContentType.set(reference._content_type_uid, uids);
  });

  return uidsByContentType;
};

export const resolveReferences = async (
  rootEntry: PlainObject,
  locale?: string
): Promise<void> => {
  for (let depth = 0; depth < MAX_REFERENCE_DEPTH; depth += 1) {
    const uidsByContentType = collectUidsByContentType(rootEntry);

    if (!uidsByContentType.size) return;

    const requests = Array.from(uidsByContentType.entries()).flatMap(
      ([contentTypeUid, uids]) =>
        splitIntoGroups(Array.from(uids), UIDS_PER_REQUEST).map((group) =>
          fetchEntries(contentTypeUid, group, locale)
        )
    );

    const fetchedEntries = (await Promise.all(requests)).flat();

    if (!fetchedEntries.length) return;

    const entriesByUid = new Map<string, PlainObject>(
      fetchedEntries
        .filter((entry) => entry?.uid)
        .map((entry) => [entry.uid as string, entry])
    );

    let replacedReferenceCount = 0;

    visitUnresolvedReferences(
      rootEntry,
      (reference, parentEntryUids, replaceWith) => {
        if (parentEntryUids.has(reference.uid)) return;

        const entry = entriesByUid.get(reference.uid);

        if (!entry) return;

        replaceWith(entry);
        replacedReferenceCount += 1;
      }
    );

    if (!replacedReferenceCount) return;
  }
};
